import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonIcon,
  IonText,
  IonCard,
  IonCardContent,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonButton,
  IonBadge,
  IonSpinner,
  IonNote,
  IonFab,
  IonFabButton,
  IonActionSheet
} from '@ionic/react';
import {
  person,
  mail,
  call,
  business,
  time,
  peopleOutline,
  searchOutline,
  scanOutline,
  downloadOutline,
  shareOutline,
  ellipsisVertical
} from 'ionicons/icons';
import { useHistory } from 'react-router';
import NetworkingService from '../services/NetworkingService';
import { NetworkingScan } from '../models/Networking';
import './NetworkingContacts.css';

const NetworkingContacts: React.FC = () => {
  const history = useHistory();
  const [contacts, setContacts] = useState<NetworkingScan[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<NetworkingScan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [showActionSheet, setShowActionSheet] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  useEffect(() => {
    // Filtrar contactos cuando cambia el texto de búsqueda
    if (searchText.trim() === '') {
      setFilteredContacts(contacts);
    } else {
      const filtered = contacts.filter(contact => {
        const profile = contact.scannedProfile;
        const searchLower = searchText.toLowerCase();

        return (
          (profile?.name || '').toLowerCase().includes(searchLower) ||
          (profile?.company || '').toLowerCase().includes(searchLower) ||
          (profile?.email || '').toLowerCase().includes(searchLower) ||
          (profile?.phone || '').toLowerCase().includes(searchLower) ||
          (profile?.position || '').toLowerCase().includes(searchLower)
        );
      });
      setFilteredContacts(filtered);
    }
  }, [searchText, contacts]);

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      const networkingService = NetworkingService.getInstance();
      const scannedContacts = await networkingService.getScannedContacts();
      setContacts(scannedContacts);
      setFilteredContacts(scannedContacts);
      console.log('NetworkingContacts: Contactos cargados:', scannedContacts.length);
      console.log('NetworkingContacts: Primer contacto:', scannedContacts[0]);
    } catch (error) {
      console.error('NetworkingContacts: Error al cargar contactos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async (event: CustomEvent) => {
    await loadContacts();
    event.detail.complete();
  };

  const navigateToScanner = () => {
    history.push('/tabs/networking/scanner');
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Función para descargar contactos como CSV
  const downloadContactsCSV = () => {
    if (contacts.length === 0) return;

    const headers = ['Nombre', 'Email', 'Teléfono', 'Empresa', 'Posición', 'Fecha de Escaneo'];
    const csvContent = [
      headers.join(','),
      ...contacts.map(contact => [
        `"${contact.scannedProfile.name || ''}"`,
        `"${contact.scannedProfile.email || ''}"`,
        `"${contact.scannedProfile.phone || ''}"`,
        `"${contact.scannedProfile.company || ''}"`,
        `"${contact.scannedProfile.position || ''}"`,
        `"${formatDate(contact.scannedAt)}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `contactos_networking_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Función para compartir contactos
  const shareContacts = async () => {
    if (navigator.share && contacts.length > 0) {
      try {
        await navigator.share({
          title: 'Contactos de Networking',
          text: `He conectado con ${contacts.length} personas en el evento.`,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/networking" />
          </IonButtons>
          <IonTitle>Mis Contactos</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Mis Contactos</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="networking-contacts-container">
          {/* Estadísticas */}
          <IonCard>
            <IonCardContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <IonIcon icon={peopleOutline} size="large" color="primary" />
                <div>
                  <h2 style={{ margin: '0', fontSize: '1.5rem' }}>
                    {contacts.length} Contactos
                  </h2>
                  <p style={{ margin: '4px 0 0 0', color: 'var(--ion-color-medium)' }}>
                    Personas que has escaneado
                  </p>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Barra de búsqueda */}
          {contacts.length > 0 && (
            <div className="search-container">
              <IonSearchbar
                value={searchText}
                onIonChange={(e) => setSearchText(e.detail.value!)}
                placeholder="Buscar por nombre, empresa o email..."
                showClearButton="focus"
                style={{ '--background': 'var(--ion-color-light)' }}
              />
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="loading-container">
              <IonSpinner name="crescent" />
              <p>Cargando contactos...</p>
            </div>
          )}

          {/* Lista de contactos como tarjetas */}
          {!isLoading && filteredContacts.length > 0 && (
            <div>
              {filteredContacts.map((contact) => {
                console.log('Rendering contact:', contact); // Debug log
                console.log('Profile data:', contact.scannedProfile); // Debug profile
                console.log('Email:', contact.scannedProfile?.email); // Debug email
                console.log('Phone:', contact.scannedProfile?.phone); // Debug phone
                const profile = contact.scannedProfile;

                return (
                  <IonCard key={contact.id} className="contact-card">
                    <IonCardContent className="contact-card-content">
                      <div className="contact-header">
                        {/* Avatar */}
                        <div className="contact-avatar">
                          <div className="contact-avatar-content">
                            {profile?.name ? getInitials(profile.name) : '??'}
                          </div>
                        </div>

                        {/* Información del contacto */}
                        <div className="contact-info">
                          <h2 className="contact-name">
                            {profile?.name || 'Nombre no disponible'}
                          </h2>

                          {profile?.position && (
                            <div className="contact-detail">
                              <IonIcon icon={person} />
                              <span className="contact-detail-text">{profile.position}</span>
                            </div>
                          )}

                          {profile?.company && (
                            <div className="contact-detail">
                              <IonIcon icon={business} />
                              <span className="contact-detail-text">{profile.company}</span>
                            </div>
                          )}

                          {/* Email - siempre mostrar */}
                          <div
                            className={`contact-detail ${profile?.email ? 'clickable' : ''}`}
                            onClick={() => {
                              if (profile?.email) {
                                window.open(`mailto:${profile.email}`, '_blank');
                              }
                            }}
                          >
                            <IonIcon icon={mail} />
                            <span className={`contact-detail-text ${!profile?.email ? 'unavailable' : ''}`}>
                              {profile?.email || '📧 Email no disponible'}
                            </span>
                          </div>

                          {/* Teléfono - siempre mostrar */}
                          <div
                            className={`contact-detail ${profile?.phone ? 'clickable' : ''}`}
                            onClick={() => {
                              if (profile?.phone) {
                                window.open(`tel:${profile.phone}`, '_blank');
                              }
                            }}
                          >
                            <IonIcon icon={call} />
                            <span className={`contact-detail-text ${!profile?.phone ? 'unavailable' : ''}`}>
                              {profile?.phone || '📞 Teléfono no disponible'}
                            </span>
                          </div>

                          <div className="contact-footer">
                            <IonIcon icon={time} />
                            Escaneado: {formatDate(contact.scannedAt)}
                          </div>
                        </div>
                      </div>
                    </IonCardContent>
                  </IonCard>
                );
              })}
            </div>
          )}

          {/* Estado vacío */}
          {!isLoading && contacts.length === 0 && (
            <div className="empty-state">
              <IonIcon icon={peopleOutline} />
              <h2>No tienes contactos aún</h2>
              <p>
                Escanea códigos QR de otros participantes para agregar sus contactos aquí.
                Podrás ver toda su información de contacto y mantener el networking después del evento.
              </p>
              <IonButton
                expand="block"
                onClick={navigateToScanner}
                style={{ maxWidth: '300px', margin: '0 auto' }}
              >
                <IonIcon icon={scanOutline} slot="start" />
                Escanear QR
              </IonButton>
            </div>
          )}

          {/* No hay resultados de búsqueda */}
          {!isLoading && contacts.length > 0 && filteredContacts.length === 0 && (
            <div className="empty-state">
              <IonIcon icon={searchOutline} style={{ fontSize: '3rem' }} />
              <h3>No se encontraron contactos</h3>
              <p>
                Intenta con otros términos de búsqueda como nombre, empresa o email.
              </p>
            </div>
          )}
        </div>

        {/* Botón flotante para acciones */}
        {contacts.length > 0 && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton className="contacts-fab" onClick={() => setShowActionSheet(true)}>
              <IonIcon icon={ellipsisVertical} />
            </IonFabButton>
          </IonFab>
        )}

        {/* ActionSheet para opciones */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          buttons={[
            {
              text: 'Descargar CSV',
              icon: downloadOutline,
              handler: () => {
                downloadContactsCSV();
              }
            },
            {
              text: 'Cancelar',
              role: 'cancel'
            }
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default NetworkingContacts;
